import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  ArrowLeft, Clock, User, Stethoscope, FlaskConical, Pill, Receipt,
  Activity, Heart, Thermometer, CheckCircle, XCircle, Plus, Edit,
  FileText, Calendar, MapPin, Download, CreditCard, DollarSign, Lock, Save
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import api from '@/lib/api';
import { toast } from 'sonner';

export default function VisitDetails() {
  const { visitId } = useParams();
  const navigate = useNavigate();
  const [visit, setVisit] = useState(null);
  const [loading, setLoading] = useState(true);
  const [vitalsDialogOpen, setVitalsDialogOpen] = useState(false);
  const [prescriptionDialogOpen, setPrescriptionDialogOpen] = useState(false);
  const [endConsultationDialogOpen, setEndConsultationDialogOpen] = useState(false);
  const [medicalRecordDialogOpen, setMedicalRecordDialogOpen] = useState(false);
  const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
  const [selectedBill, setSelectedBill] = useState(null);
  const [paymentAmount, setPaymentAmount] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('cash');
  const [doctors, setDoctors] = useState([]);
  const [draftMedicalRecord, setDraftMedicalRecord] = useState(null);
  const [savingRecord, setSavingRecord] = useState(false);

  const [vitalsForm, setVitalsForm] = useState({
    blood_pressure: '',
    pulse: '',
    temperature: '',
    respiratory_rate: '',
    oxygen_saturation: '',
    weight: '',
    height: ''
  });

  const [prescriptionForm, setPrescriptionForm] = useState({
    diagnosis: '',
    medications: [{ name: '', dosage: '', frequency: '', duration: '', route: 'oral', instructions: '' }],
    instructions: '',
    follow_up_date: '',
    notes: ''
  });

  const [endForm, setEndForm] = useState({
    diagnosis: '',
    treatment_plan: ''
  });

  useEffect(() => {
    fetchVisit();
    fetchDoctors();
  }, [visitId]);

  const fetchVisit = async () => {
    try {
      const response = await api.get(`/visits/${visitId}`);
      setVisit(response.data);
    } catch (error) {
      toast.error('Failed to load visit details');
    } finally {
      setLoading(false);
    }
  };

  const fetchDoctors = async () => {
    try {
      const response = await api.get('/users/doctors');
      setDoctors(response.data);
    } catch (error) {
      console.error('Failed to fetch doctors');
    }
  };

  const handleRecordVitals = async (e) => {
    e.preventDefault();
    try {
      const vitals = {
        blood_pressure: vitalsForm.blood_pressure || null,
        pulse: vitalsForm.pulse ? parseInt(vitalsForm.pulse) : null,
        temperature: vitalsForm.temperature ? parseFloat(vitalsForm.temperature) : null,
        respiratory_rate: vitalsForm.respiratory_rate ? parseInt(vitalsForm.respiratory_rate) : null,
        oxygen_saturation: vitalsForm.oxygen_saturation ? parseInt(vitalsForm.oxygen_saturation) : null,
        weight: vitalsForm.weight ? parseFloat(vitalsForm.weight) : null,
        height: vitalsForm.height ? parseFloat(vitalsForm.height) : null
      };
      await api.post(`/visits/${visitId}/vitals`, vitals);
      toast.success('Vitals recorded');
      setVitalsDialogOpen(false);
      fetchVisit();
    } catch (error) {
      toast.error('Failed to record vitals');
    }
  };

  const handleCreatePrescription = async (e) => {
    e.preventDefault();
    if (!prescriptionForm.diagnosis || prescriptionForm.medications.length === 0) {
      toast.error('Please fill in diagnosis and at least one medication');
      return;
    }

    const user = JSON.parse(localStorage.getItem('user'));
    try {
      await api.post('/prescriptions', {
        visit_id: visitId,
        patient_id: visit.patient_id,
        prescriber_id: user.id,
        diagnosis: prescriptionForm.diagnosis,
        medications: prescriptionForm.medications.filter(m => m.name),
        instructions: prescriptionForm.instructions,
        follow_up_date: prescriptionForm.follow_up_date || null,
        notes: prescriptionForm.notes
      });
      toast.success('Prescription created');
      setPrescriptionDialogOpen(false);
      setPrescriptionForm({
        diagnosis: '',
        medications: [{ name: '', dosage: '', frequency: '', duration: '', route: 'oral', instructions: '' }],
        instructions: '',
        follow_up_date: '',
        notes: ''
      });
      fetchVisit();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create prescription');
    }
  };

  const handleEndConsultation = async () => {
    try {
      const response = await api.put(`/visits/${visitId}/end-consultation`, null, {
        params: {
          diagnosis: endForm.diagnosis || undefined,
          treatment_plan: endForm.treatment_plan || undefined
        }
      });
      
      // Auto-generated draft medical record is returned
      if (response.data.medical_record) {
        setDraftMedicalRecord(response.data.medical_record);
        setEndConsultationDialogOpen(false);
        setMedicalRecordDialogOpen(true);
        toast.success('Consultation ended - Review draft medical record');
      } else {
        toast.success('Consultation ended');
        setEndConsultationDialogOpen(false);
        fetchVisit();
      }
    } catch (error) {
      toast.error('Failed to end consultation');
    }
  };

  const handleSaveDraftRecord = async () => {
    if (!draftMedicalRecord) return;
    setSavingRecord(true);
    try {
      await api.put(`/medical-records/${draftMedicalRecord.id}`, {
        diagnosis: draftMedicalRecord.diagnosis,
        symptoms: draftMedicalRecord.symptoms,
        prescription: draftMedicalRecord.prescription,
        lab_results: draftMedicalRecord.lab_results,
        notes: draftMedicalRecord.notes,
        vitals: draftMedicalRecord.vitals,
        allergies: draftMedicalRecord.allergies
      });
      toast.success('Draft medical record saved');
    } catch (error) {
      toast.error('Failed to save medical record');
    } finally {
      setSavingRecord(false);
    }
  };

  const handleFinalizeRecord = async () => {
    if (!draftMedicalRecord) return;
    setSavingRecord(true);
    try {
      // First save any changes
      await api.put(`/medical-records/${draftMedicalRecord.id}`, {
        diagnosis: draftMedicalRecord.diagnosis,
        symptoms: draftMedicalRecord.symptoms,
        prescription: draftMedicalRecord.prescription,
        lab_results: draftMedicalRecord.lab_results,
        notes: draftMedicalRecord.notes,
        vitals: draftMedicalRecord.vitals,
        allergies: draftMedicalRecord.allergies
      });
      
      // Then finalize
      await api.put(`/medical-records/${draftMedicalRecord.id}/finalize`);
      toast.success('Medical record finalized and locked');
      setMedicalRecordDialogOpen(false);
      setDraftMedicalRecord(null);
      fetchVisit();
    } catch (error) {
      toast.error('Failed to finalize medical record');
    } finally {
      setSavingRecord(false);
    }
  };

  const handleCheckout = async () => {
    try {
      await api.put(`/visits/${visitId}/checkout`);
      toast.success('Visit completed');
      fetchVisit();
    } catch (error) {
      toast.error('Failed to checkout');
    }
  };

  const handleStartConsultation = async () => {
    try {
      await api.put(`/visits/${visitId}/start-consultation`);
      toast.success('Consultation started');
      fetchVisit();
    } catch (error) {
      toast.error('Failed to start consultation');
    }
  };

  const addMedication = () => {
    setPrescriptionForm({
      ...prescriptionForm,
      medications: [...prescriptionForm.medications, { name: '', dosage: '', frequency: '', duration: '', route: 'oral', instructions: '' }]
    });
  };

  const updateMedication = (index, field, value) => {
    const updated = [...prescriptionForm.medications];
    updated[index][field] = value;
    setPrescriptionForm({ ...prescriptionForm, medications: updated });
  };

  const removeMedication = (index) => {
    const updated = prescriptionForm.medications.filter((_, i) => i !== index);
    setPrescriptionForm({ ...prescriptionForm, medications: updated });
  };

  const handleRecordPayment = async () => {
    if (!paymentAmount || parseFloat(paymentAmount) <= 0) {
      toast.error('Please enter a valid payment amount');
      return;
    }
    if (parseFloat(paymentAmount) > selectedBill.balance) {
      toast.error('Payment amount cannot exceed balance');
      return;
    }
    try {
      await api.post(`/billing/${selectedBill.id}/payment`, {
        amount: parseFloat(paymentAmount),
        payment_method: paymentMethod
      });
      toast.success('Payment recorded successfully');
      setPaymentDialogOpen(false);
      setPaymentAmount('');
      setSelectedBill(null);
      fetchVisit();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to record payment');
    }
  };

  const handleDownloadPrescription = async (prescriptionId) => {
    try {
      const response = await api.get(`/prescriptions/${prescriptionId}/pdf`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `prescription_${prescriptionId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Prescription downloaded');
    } catch (error) {
      toast.error('Failed to download prescription');
    }
  };

  const openPaymentDialog = (bill) => {
    setSelectedBill(bill);
    setPaymentAmount(bill.balance?.toString() || '');
    setPaymentDialogOpen(true);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'waiting': return 'bg-amber-100 text-amber-700';
      case 'in_consultation': return 'bg-purple-100 text-purple-700';
      case 'completed': return 'bg-emerald-100 text-emerald-700';
      case 'cancelled': return 'bg-rose-100 text-rose-700';
      default: return 'bg-slate-100 text-slate-700';
    }
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleString();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#4A238F]"></div>
      </div>
    );
  }

  if (!visit) {
    return (
      <div className="text-center py-12">
        <p className="text-slate-500">Visit not found</p>
      </div>
    );
  }

  return (
    <div data-testid="visit-details-page">
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
      >
        {/* Header */}
        <div className="flex items-center gap-4 mb-6">
          <Button variant="ghost" onClick={() => navigate('/visits')}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </Button>
          <div className="flex-1">
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-bold text-slate-900">{visit.visit_number}</h1>
              <Badge className={getStatusColor(visit.status)}>
                {visit.status === 'in_consultation' ? 'In Consultation' : visit.status}
              </Badge>
              <Badge className={`${visit.visit_type === 'Emergency' ? 'bg-rose-100 text-rose-700' : visit.visit_type === 'IPD' ? 'bg-purple-100 text-purple-700' : 'bg-purple-200 text-blue-700'}`}>
                {visit.visit_type}
              </Badge>
              {visit.token_number && (
                <Badge variant="outline">Token #{visit.token_number}</Badge>
              )}
            </div>
          </div>
          <div className="flex gap-2">
            {visit.status === 'waiting' && (
              <Button onClick={handleStartConsultation} className="bg-emerald-600 hover:bg-emerald-700 text-white">
                <Stethoscope className="mr-2 h-4 w-4" />
                Start Consultation
              </Button>
            )}
            {visit.status === 'in_consultation' && (
              <>
                <Button variant="outline" onClick={() => setVitalsDialogOpen(true)}>
                  <Heart className="mr-2 h-4 w-4" />
                  Record Vitals
                </Button>
                <Button variant="outline" onClick={() => setPrescriptionDialogOpen(true)}>
                  <Pill className="mr-2 h-4 w-4" />
                  New Prescription
                </Button>
                <Button onClick={() => setEndConsultationDialogOpen(true)} className="bg-amber-600 hover:bg-amber-700 text-white">
                  End Consultation
                </Button>
              </>
            )}
            {(visit.status === 'in_consultation' || visit.consultation_end_time) && visit.status !== 'completed' && (
              <Button onClick={handleCheckout} className="bg-[#4A238F] hover:bg-[#3A1B72] text-white">
                <CheckCircle className="mr-2 h-4 w-4" />
                Checkout
              </Button>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column - Patient & Visit Info */}
          <div className="space-y-6">
            {/* Patient Card */}
            <Card className="border border-slate-200 bg-white">
              <CardHeader className="pb-3">
                <CardTitle className="text-lg flex items-center gap-2">
                  <User className="h-5 w-5 text-[#4A238F]" />
                  Patient Information
                </CardTitle>
              </CardHeader>
              <CardContent>
                {visit.patient && (
                  <div className="space-y-2">
                    <p className="font-semibold text-lg text-slate-900">{visit.patient.full_name}</p>
                    <p className="text-sm text-slate-600">{visit.patient.patient_number}</p>
                    <div className="flex gap-2 text-sm text-slate-600">
                      <span>{visit.patient.gender}</span>
                      <span>•</span>
                      <span>{visit.patient.date_of_birth}</span>
                    </div>
                    {visit.patient.phone && (
                      <p className="text-sm text-slate-600">📞 {visit.patient.phone}</p>
                    )}
                    {visit.patient.blood_group && (
                      <Badge className="bg-rose-100 text-rose-700">{visit.patient.blood_group}</Badge>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Visit Details Card */}
            <Card className="border border-slate-200 bg-white">
              <CardHeader className="pb-3">
                <CardTitle className="text-lg flex items-center gap-2">
                  <Calendar className="h-5 w-5 text-[#4A238F]" />
                  Visit Details
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div>
                  <p className="text-xs text-slate-500">Chief Complaint</p>
                  <p className="font-medium">{visit.chief_complaint}</p>
                </div>
                {visit.department && (
                  <div>
                    <p className="text-xs text-slate-500">Department</p>
                    <p className="font-medium">{visit.department}</p>
                  </div>
                )}
                {visit.doctor && (
                  <div>
                    <p className="text-xs text-slate-500">Assigned Doctor</p>
                    <p className="font-medium">{visit.doctor.full_name?.startsWith('Dr.') ? visit.doctor.full_name : `Dr. ${visit.doctor.full_name}`}</p>
                  </div>
                )}
                {visit.diagnosis && (
                  <div>
                    <p className="text-xs text-slate-500">Diagnosis</p>
                    <p className="font-medium">{visit.diagnosis}</p>
                  </div>
                )}
                <div className="pt-2 border-t">
                  <p className="text-xs text-slate-500">Check-in</p>
                  <p className="text-sm">{formatDateTime(visit.check_in_time)}</p>
                </div>
                {visit.consultation_start_time && (
                  <div>
                    <p className="text-xs text-slate-500">Consultation Started</p>
                    <p className="text-sm">{formatDateTime(visit.consultation_start_time)}</p>
                  </div>
                )}
                {visit.check_out_time && (
                  <div>
                    <p className="text-xs text-slate-500">Check-out</p>
                    <p className="text-sm">{formatDateTime(visit.check_out_time)}</p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Vitals Card */}
            <Card className="border border-slate-200 bg-white">
              <CardHeader className="pb-3">
                <CardTitle className="text-lg flex items-center gap-2">
                  <Activity className="h-5 w-5 text-rose-600" />
                  Vitals
                </CardTitle>
              </CardHeader>
              <CardContent>
                {visit.vitals ? (
                  <div className="grid grid-cols-2 gap-3">
                    {visit.vitals.blood_pressure && (
                      <div className="p-2 bg-slate-50 rounded">
                        <p className="text-xs text-slate-500">Blood Pressure</p>
                        <p className="font-semibold">{visit.vitals.blood_pressure}</p>
                      </div>
                    )}
                    {visit.vitals.pulse && (
                      <div className="p-2 bg-slate-50 rounded">
                        <p className="text-xs text-slate-500">Pulse</p>
                        <p className="font-semibold">{visit.vitals.pulse} bpm</p>
                      </div>
                    )}
                    {visit.vitals.temperature && (
                      <div className="p-2 bg-slate-50 rounded">
                        <p className="text-xs text-slate-500">Temperature</p>
                        <p className="font-semibold">{visit.vitals.temperature}°F</p>
                      </div>
                    )}
                    {visit.vitals.oxygen_saturation && (
                      <div className="p-2 bg-slate-50 rounded">
                        <p className="text-xs text-slate-500">SpO2</p>
                        <p className="font-semibold">{visit.vitals.oxygen_saturation}%</p>
                      </div>
                    )}
                  </div>
                ) : (
                  <p className="text-sm text-slate-500">No vitals recorded</p>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Middle & Right Columns - Tabs */}
          <div className="lg:col-span-2">
            <Tabs defaultValue="timeline" className="space-y-4">
              <TabsList className="bg-slate-200 p-1">
                <TabsTrigger value="timeline" className="text-slate-700 data-[state=active]:bg-white data-[state=active]:text-[#4A238F] data-[state=active]:shadow-sm">Timeline</TabsTrigger>
                <TabsTrigger value="prescriptions" className="text-slate-700 data-[state=active]:bg-white data-[state=active]:text-[#4A238F] data-[state=active]:shadow-sm">Prescriptions ({visit.prescriptions?.length || 0})</TabsTrigger>
                <TabsTrigger value="labs" className="text-slate-700 data-[state=active]:bg-white data-[state=active]:text-[#4A238F] data-[state=active]:shadow-sm">Lab Tests ({visit.lab_entries?.length || 0})</TabsTrigger>
                <TabsTrigger value="billing" className="text-slate-700 data-[state=active]:bg-white data-[state=active]:text-[#4A238F] data-[state=active]:shadow-sm">Billing ({visit.billing?.length || 0})</TabsTrigger>
              </TabsList>

              <TabsContent value="timeline">
                <Card className="border border-slate-200 bg-white">
                  <CardHeader>
                    <CardTitle className="text-lg">Visit Timeline</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      {visit.timeline?.map((event, index) => (
                        <div key={index} className="flex gap-4">
                          <div className="flex flex-col items-center">
                            <div className={`w-3 h-3 rounded-full ${
                              event.event_type === 'check_in' ? 'bg-purple-600' :
                              event.event_type === 'consultation_start' ? 'bg-indigo-500' :
                              event.event_type === 'vitals_recorded' ? 'bg-rose-500' :
                              event.event_type === 'prescription_created' ? 'bg-emerald-500' :
                              event.event_type === 'lab_ordered' ? 'bg-amber-500' :
                              event.event_type === 'check_out' ? 'bg-slate-500' :
                              'bg-slate-300'
                            }`} />
                            {index < visit.timeline.length - 1 && (
                              <div className="w-0.5 h-full bg-slate-200 mt-1" />
                            )}
                          </div>
                          <div className="flex-1 pb-4">
                            <p className="font-medium text-slate-900">{event.description}</p>
                            <div className="flex items-center gap-2 text-xs text-slate-500 mt-1">
                              <span>{new Date(event.timestamp).toLocaleString()}</span>
                              {event.performed_by && (
                                <>
                                  <span>•</span>
                                  <span>{event.performed_by}</span>
                                </>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>

              <TabsContent value="prescriptions">
                <Card className="border border-slate-200 bg-white">
                  <CardHeader className="flex flex-row items-center justify-between">
                    <CardTitle className="text-lg">Prescriptions</CardTitle>
                    {visit.status === 'in_consultation' && (
                      <Button size="sm" onClick={() => setPrescriptionDialogOpen(true)}>
                        <Plus className="mr-1 h-4 w-4" />
                        Add
                      </Button>
                    )}
                  </CardHeader>
                  <CardContent>
                    {visit.prescriptions?.length > 0 ? (
                      <div className="space-y-4">
                        {visit.prescriptions.map((rx) => (
                          <div key={rx.id} className="p-4 border rounded-lg">
                            <div className="flex justify-between items-start mb-2">
                              <div>
                                <p className="font-semibold">{rx.prescription_number}</p>
                                <p className="text-sm text-slate-600">{rx.diagnosis}</p>
                              </div>
                              <div className="flex items-center gap-2">
                                <Badge className={rx.status === 'active' ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-700'}>
                                  {rx.status}
                                </Badge>
                                <Button 
                                  size="sm" 
                                  variant="outline" 
                                  onClick={() => handleDownloadPrescription(rx.id)}
                                  data-testid={`download-rx-${rx.id}`}
                                >
                                  <Download className="h-4 w-4" />
                                </Button>
                              </div>
                            </div>
                            <div className="mt-3">
                              <p className="text-xs text-slate-500 mb-1">Medications:</p>
                              {rx.medications?.map((med, i) => (
                                <p key={i} className="text-sm">
                                  • {med.name} {med.dosage} - {med.frequency} for {med.duration}
                                </p>
                              ))}
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-sm text-slate-500">No prescriptions for this visit</p>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>

              <TabsContent value="labs">
                <Card className="border border-slate-200 bg-white">
                  <CardHeader>
                    <CardTitle className="text-lg">Lab Tests</CardTitle>
                  </CardHeader>
                  <CardContent>
                    {visit.lab_entries?.length > 0 ? (
                      <div className="space-y-3">
                        {visit.lab_entries.map((lab) => (
                          <div key={lab.id} className="p-3 border rounded-lg flex justify-between items-center">
                            <div>
                              <p className="font-medium">{lab.test_name}</p>
                              <p className="text-sm text-slate-600">{lab.test_category}</p>
                            </div>
                            <Badge className={
                              lab.status === 'verified' ? 'bg-emerald-100 text-emerald-700' :
                              lab.status === 'completed' ? 'bg-purple-200 text-blue-700' :
                              'bg-amber-100 text-amber-700'
                            }>
                              {lab.status}
                            </Badge>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-sm text-slate-500">No lab tests ordered for this visit</p>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>

              <TabsContent value="billing">
                <Card className="border border-slate-200 bg-white">
                  <CardHeader>
                    <CardTitle className="text-lg">Billing</CardTitle>
                  </CardHeader>
                  <CardContent>
                    {visit.billing?.length > 0 ? (
                      <div className="space-y-3">
                        {visit.billing.map((bill) => (
                          <div key={bill.id} className="p-4 border rounded-lg">
                            <div className="flex justify-between items-start mb-2">
                              <div>
                                <p className="font-medium">{bill.invoice_number}</p>
                                <p className="text-lg font-bold text-slate-900">${bill.total_amount?.toFixed(2)}</p>
                              </div>
                              <Badge className={
                                bill.payment_status === 'paid' ? 'bg-emerald-100 text-emerald-700' :
                                bill.payment_status === 'partial' ? 'bg-amber-100 text-amber-700' :
                                'bg-rose-100 text-rose-700'
                              }>
                                {bill.payment_status}
                              </Badge>
                            </div>
                            <div className="flex justify-between items-center">
                              <div>
                                {bill.paid_amount > 0 && (
                                  <p className="text-sm text-emerald-600">Paid: ${bill.paid_amount?.toFixed(2)}</p>
                                )}
                                {bill.balance > 0 && (
                                  <p className="text-sm text-rose-600">Balance: ${bill.balance?.toFixed(2)}</p>
                                )}
                              </div>
                              {bill.balance > 0 && (
                                <Button 
                                  size="sm" 
                                  onClick={() => openPaymentDialog(bill)}
                                  className="bg-emerald-600 hover:bg-emerald-700 text-white"
                                  data-testid={`pay-bill-${bill.id}`}
                                >
                                  <CreditCard className="mr-2 h-4 w-4" />
                                  Record Payment
                                </Button>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-sm text-slate-500">No billing records for this visit</p>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>
            </Tabs>
          </div>
        </div>

        {/* Vitals Dialog */}
        <Dialog open={vitalsDialogOpen} onOpenChange={setVitalsDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Record Vitals</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleRecordVitals} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Blood Pressure</Label>
                  <Input
                    value={vitalsForm.blood_pressure}
                    onChange={(e) => setVitalsForm({...vitalsForm, blood_pressure: e.target.value})}
                    placeholder="120/80"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Pulse (bpm)</Label>
                  <Input
                    type="number"
                    value={vitalsForm.pulse}
                    onChange={(e) => setVitalsForm({...vitalsForm, pulse: e.target.value})}
                    placeholder="72"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Temperature (°F)</Label>
                  <Input
                    type="number"
                    step="0.1"
                    value={vitalsForm.temperature}
                    onChange={(e) => setVitalsForm({...vitalsForm, temperature: e.target.value})}
                    placeholder="98.6"
                  />
                </div>
                <div className="space-y-2">
                  <Label>SpO2 (%)</Label>
                  <Input
                    type="number"
                    value={vitalsForm.oxygen_saturation}
                    onChange={(e) => setVitalsForm({...vitalsForm, oxygen_saturation: e.target.value})}
                    placeholder="98"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Weight (kg)</Label>
                  <Input
                    type="number"
                    step="0.1"
                    value={vitalsForm.weight}
                    onChange={(e) => setVitalsForm({...vitalsForm, weight: e.target.value})}
                    placeholder="70"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Height (cm)</Label>
                  <Input
                    type="number"
                    value={vitalsForm.height}
                    onChange={(e) => setVitalsForm({...vitalsForm, height: e.target.value})}
                    placeholder="170"
                  />
                </div>
              </div>
              <Button type="submit" className="w-full bg-[#4A238F] hover:bg-[#3A1B72] text-white">
                Save Vitals
              </Button>
            </form>
          </DialogContent>
        </Dialog>

        {/* Prescription Dialog */}
        <Dialog open={prescriptionDialogOpen} onOpenChange={setPrescriptionDialogOpen}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Create Prescription</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleCreatePrescription} className="space-y-4">
              <div className="space-y-2">
                <Label>Diagnosis *</Label>
                <Input
                  value={prescriptionForm.diagnosis}
                  onChange={(e) => setPrescriptionForm({...prescriptionForm, diagnosis: e.target.value})}
                  placeholder="Enter diagnosis"
                  required
                />
              </div>

              <div className="space-y-2">
                <div className="flex justify-between items-center">
                  <Label>Medications *</Label>
                  <Button type="button" size="sm" variant="outline" onClick={addMedication}>
                    <Plus className="h-4 w-4 mr-1" />
                    Add
                  </Button>
                </div>
                {prescriptionForm.medications.map((med, index) => (
                  <div key={index} className="p-3 border rounded-lg space-y-2">
                    <div className="grid grid-cols-3 gap-2">
                      <Input
                        value={med.name}
                        onChange={(e) => updateMedication(index, 'name', e.target.value)}
                        placeholder="Medication name"
                      />
                      <Input
                        value={med.dosage}
                        onChange={(e) => updateMedication(index, 'dosage', e.target.value)}
                        placeholder="Dosage (e.g., 500mg)"
                      />
                      <Input
                        value={med.frequency}
                        onChange={(e) => updateMedication(index, 'frequency', e.target.value)}
                        placeholder="Frequency (e.g., twice daily)"
                      />
                    </div>
                    <div className="grid grid-cols-3 gap-2">
                      <Input
                        value={med.duration}
                        onChange={(e) => updateMedication(index, 'duration', e.target.value)}
                        placeholder="Duration (e.g., 7 days)"
                      />
                      <Select value={med.route} onValueChange={(v) => updateMedication(index, 'route', v)}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="oral">Oral</SelectItem>
                          <SelectItem value="IV">IV</SelectItem>
                          <SelectItem value="IM">IM</SelectItem>
                          <SelectItem value="topical">Topical</SelectItem>
                          <SelectItem value="inhaled">Inhaled</SelectItem>
                        </SelectContent>
                      </Select>
                      {index > 0 && (
                        <Button type="button" variant="ghost" size="sm" onClick={() => removeMedication(index)} className="text-rose-600">
                          Remove
                        </Button>
                      )}
                    </div>
                    <Input
                      value={med.instructions}
                      onChange={(e) => updateMedication(index, 'instructions', e.target.value)}
                      placeholder="Special instructions (e.g., Take after meals)"
                    />
                  </div>
                ))}
              </div>

              <div className="space-y-2">
                <Label>General Instructions</Label>
                <textarea
                  value={prescriptionForm.instructions}
                  onChange={(e) => setPrescriptionForm({...prescriptionForm, instructions: e.target.value})}
                  className="w-full min-h-[60px] px-3 py-2 border border-slate-200 rounded-md bg-white text-sm"
                  placeholder="General instructions for the patient"
                />
              </div>

              <div className="space-y-2">
                <Label>Follow-up Date</Label>
                <Input
                  type="date"
                  value={prescriptionForm.follow_up_date}
                  onChange={(e) => setPrescriptionForm({...prescriptionForm, follow_up_date: e.target.value})}
                />
              </div>

              <Button type="submit" className="w-full bg-[#4A238F] hover:bg-[#3A1B72] text-white">
                Create Prescription
              </Button>
            </form>
          </DialogContent>
        </Dialog>

        {/* End Consultation Dialog */}
        <Dialog open={endConsultationDialogOpen} onOpenChange={setEndConsultationDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>End Consultation</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>Final Diagnosis</Label>
                <Input
                  value={endForm.diagnosis}
                  onChange={(e) => setEndForm({...endForm, diagnosis: e.target.value})}
                  placeholder="Enter final diagnosis"
                />
              </div>
              <div className="space-y-2">
                <Label>Treatment Plan</Label>
                <textarea
                  value={endForm.treatment_plan}
                  onChange={(e) => setEndForm({...endForm, treatment_plan: e.target.value})}
                  className="w-full min-h-[80px] px-3 py-2 border border-slate-200 rounded-md bg-white text-sm"
                  placeholder="Treatment plan and recommendations"
                />
              </div>
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => setEndConsultationDialogOpen(false)} className="flex-1">
                  Cancel
                </Button>
                <Button onClick={handleEndConsultation} className="flex-1 bg-amber-600 hover:bg-amber-700 text-white">
                  End Consultation
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Payment Dialog */}
        <Dialog open={paymentDialogOpen} onOpenChange={setPaymentDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <DollarSign className="h-5 w-5 text-emerald-600" />
                Record Payment
              </DialogTitle>
            </DialogHeader>
            {selectedBill && (
              <div className="space-y-4">
                <div className="p-4 bg-slate-50 rounded-lg space-y-2">
                  <div className="flex justify-between">
                    <span className="text-sm text-slate-600">Invoice #</span>
                    <span className="font-medium">{selectedBill.invoice_number}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-slate-600">Total Amount</span>
                    <span className="font-medium">${selectedBill.total_amount?.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-slate-600">Already Paid</span>
                    <span className="font-medium text-emerald-600">${selectedBill.paid_amount?.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between border-t pt-2">
                    <span className="text-sm font-medium text-rose-600">Balance Due</span>
                    <span className="font-bold text-rose-600">${selectedBill.balance?.toFixed(2)}</span>
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label>Payment Amount ($)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    max={selectedBill.balance}
                    value={paymentAmount}
                    onChange={(e) => setPaymentAmount(e.target.value)}
                    placeholder="Enter payment amount"
                    data-testid="payment-amount"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label>Payment Method</Label>
                  <Select value={paymentMethod} onValueChange={setPaymentMethod}>
                    <SelectTrigger data-testid="payment-method">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="cash">Cash</SelectItem>
                      <SelectItem value="credit_card">Credit Card</SelectItem>
                      <SelectItem value="debit_card">Debit Card</SelectItem>
                      <SelectItem value="insurance">Insurance</SelectItem>
                      <SelectItem value="bank_transfer">Bank Transfer</SelectItem>
                      <SelectItem value="check">Check</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="flex gap-2 pt-2">
                  <Button variant="outline" onClick={() => setPaymentDialogOpen(false)} className="flex-1">
                    Cancel
                  </Button>
                  <Button 
                    onClick={handleRecordPayment} 
                    className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white"
                    data-testid="submit-payment"
                  >
                    <CreditCard className="mr-2 h-4 w-4" />
                    Record Payment
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Draft Medical Record Dialog */}
        <Dialog open={medicalRecordDialogOpen} onOpenChange={setMedicalRecordDialogOpen}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <FileText className="h-5 w-5 text-[#4A238F]" />
                Draft Medical Record
                <Badge className="ml-2 bg-amber-100 text-amber-700">Draft</Badge>
              </DialogTitle>
            </DialogHeader>
            
            {draftMedicalRecord && (
              <div className="space-y-6 py-4">
                {/* Patient Info Banner */}
                <div className="bg-slate-50 rounded-lg p-4 border border-slate-200">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="font-semibold text-lg text-slate-900">
                        {draftMedicalRecord.patient_name}
                      </h3>
                      <p className="text-sm text-slate-600">
                        Age: {draftMedicalRecord.patient_age} • Visit: {visit?.visit_number}
                      </p>
                    </div>
                    <div className="text-right text-sm text-slate-500">
                      {new Date(draftMedicalRecord.created_at).toLocaleString()}
                    </div>
                  </div>
                </div>

                {/* Allergies Warning */}
                {draftMedicalRecord.allergies && (
                  <div className="bg-rose-50 border border-rose-200 rounded-lg p-3">
                    <Label className="text-sm font-semibold text-rose-700">⚠️ Allergies</Label>
                    <p className="text-rose-800 mt-1">{draftMedicalRecord.allergies}</p>
                  </div>
                )}

                {/* Vitals Section */}
                {draftMedicalRecord.vitals && Object.keys(draftMedicalRecord.vitals).length > 0 && (
                  <div className="space-y-2">
                    <Label className="text-sm font-semibold text-slate-700">Vitals</Label>
                    <div className="grid grid-cols-4 gap-3">
                      {draftMedicalRecord.vitals.blood_pressure && (
                        <div className="bg-purple-100 rounded-lg p-3 text-center">
                          <p className="text-xs text-purple-700">Blood Pressure</p>
                          <p className="font-semibold text-blue-900">{draftMedicalRecord.vitals.blood_pressure}</p>
                        </div>
                      )}
                      {draftMedicalRecord.vitals.pulse && (
                        <div className="bg-rose-50 rounded-lg p-3 text-center">
                          <p className="text-xs text-rose-600">Pulse</p>
                          <p className="font-semibold text-rose-900">{draftMedicalRecord.vitals.pulse} bpm</p>
                        </div>
                      )}
                      {draftMedicalRecord.vitals.temperature && (
                        <div className="bg-amber-50 rounded-lg p-3 text-center">
                          <p className="text-xs text-amber-600">Temperature</p>
                          <p className="font-semibold text-amber-900">{draftMedicalRecord.vitals.temperature}°F</p>
                        </div>
                      )}
                      {draftMedicalRecord.vitals.oxygen_saturation && (
                        <div className="bg-emerald-50 rounded-lg p-3 text-center">
                          <p className="text-xs text-emerald-600">SpO2</p>
                          <p className="font-semibold text-emerald-900">{draftMedicalRecord.vitals.oxygen_saturation}%</p>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Symptoms / Chief Complaint */}
                <div className="space-y-2">
                  <Label className="text-sm font-semibold text-slate-700">Symptoms / Chief Complaint</Label>
                  <textarea
                    value={draftMedicalRecord.symptoms || ''}
                    onChange={(e) => setDraftMedicalRecord({...draftMedicalRecord, symptoms: e.target.value})}
                    className="w-full min-h-[80px] px-3 py-2 border border-slate-200 rounded-md bg-white text-sm"
                    placeholder="Patient's reported symptoms..."
                  />
                </div>

                {/* Diagnosis */}
                <div className="space-y-2">
                  <Label className="text-sm font-semibold text-slate-700">Diagnosis</Label>
                  <textarea
                    value={draftMedicalRecord.diagnosis || ''}
                    onChange={(e) => setDraftMedicalRecord({...draftMedicalRecord, diagnosis: e.target.value})}
                    className="w-full min-h-[80px] px-3 py-2 border border-slate-200 rounded-md bg-white text-sm"
                    placeholder="Clinical diagnosis..."
                  />
                </div>

                {/* Prescribed Medications */}
                <div className="space-y-2">
                  <Label className="text-sm font-semibold text-slate-700">Prescribed Medications</Label>
                  <textarea
                    value={draftMedicalRecord.prescription || ''}
                    onChange={(e) => setDraftMedicalRecord({...draftMedicalRecord, prescription: e.target.value})}
                    className="w-full min-h-[100px] px-3 py-2 border border-slate-200 rounded-md bg-white text-sm font-mono"
                    placeholder="List of medications..."
                  />
                </div>

                {/* Lab Results */}
                <div className="space-y-2">
                  <Label className="text-sm font-semibold text-slate-700">Lab Results</Label>
                  <textarea
                    value={draftMedicalRecord.lab_results || ''}
                    onChange={(e) => setDraftMedicalRecord({...draftMedicalRecord, lab_results: e.target.value})}
                    className="w-full min-h-[80px] px-3 py-2 border border-slate-200 rounded-md bg-white text-sm font-mono"
                    placeholder="Lab test results..."
                  />
                </div>

                {/* Manual Notes */}
                <div className="space-y-2">
                  <Label className="text-sm font-semibold text-slate-700">Additional Notes</Label>
                  <textarea
                    value={draftMedicalRecord.notes || ''}
                    onChange={(e) => setDraftMedicalRecord({...draftMedicalRecord, notes: e.target.value})}
                    className="w-full min-h-[100px] px-3 py-2 border border-slate-200 rounded-md bg-white text-sm"
                    placeholder="Enter any additional clinical notes, observations, or follow-up instructions..."
                  />
                </div>

                {/* Action Buttons */}
                <div className="flex gap-3 pt-4 border-t border-slate-200">
                  <Button 
                    variant="outline" 
                    onClick={() => {
                      setMedicalRecordDialogOpen(false);
                      setDraftMedicalRecord(null);
                      fetchVisit();
                    }}
                    className="flex-1"
                  >
                    Close (Save as Draft)
                  </Button>
                  <Button 
                    variant="outline"
                    onClick={handleSaveDraftRecord}
                    disabled={savingRecord}
                    className="flex-1"
                  >
                    <Save className="mr-2 h-4 w-4" />
                    Save Changes
                  </Button>
                  <Button 
                    onClick={handleFinalizeRecord}
                    disabled={savingRecord}
                    className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white"
                    data-testid="finalize-record-btn"
                  >
                    <Lock className="mr-2 h-4 w-4" />
                    Finalize Record
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </motion.div>
    </div>
  );
}
